import React from "react";
import { CheckCircle2, XCircle, AlertTriangle } from "lucide-react";
import { evaluateContrast, type ContrastResult } from "@cms/ui/lib/color";
import { A11Y_MIN_RATIO, CONTRAST_PAIRS, type A11yTarget } from "../lib/design-tokens";

/**
 * WCAG contrast report for the token combinations the site renders (body
 * text, header, links, …), judged against the tenant's accessibility target.
 * Each row shows a live sample and the ratio; hovering reveals the pass/fail
 * verdict against every WCAG level.
 */
export function TokenContrastChecks({ merged, target }: {
  merged: Record<string, string>;
  target: A11yTarget;
}) {
  const rows = CONTRAST_PAIRS.map((pair) => {
    const fg = pair.fgLiteral ?? merged[pair.fg ?? ""] ?? "";
    const bg = merged[pair.bg] ?? "";
    return { pair, fg, bg, result: fg && bg ? evaluateContrast(fg, bg) : null };
  });

  return (
    <div className="rounded-lg border">
      {rows.map(({ pair, fg, bg, result }, index) => (
        <ContrastRow
          key={pair.id}
          label={pair.label}
          fg={fg}
          bg={bg}
          result={result}
          target={target}
          last={index === rows.length - 1}
        />
      ))}
    </div>
  );
}

function ContrastRow({ label, fg, bg, result, target, last }: {
  label: string;
  fg: string;
  bg: string;
  result: ContrastResult | null;
  target: A11yTarget;
  last: boolean;
}) {
  const targetPass = (result?.ratio ?? 0) >= A11Y_MIN_RATIO[target];
  const allPass = result?.levels.every((level) => level.pass) ?? false;

  return (
    <div className={`group relative flex items-center gap-2 px-2.5 py-1.5 text-xs ${last ? "" : "border-b"}`}>
      {/* Live sample: the pair's actual colours. */}
      <span
        className="flex h-6 w-9 shrink-0 items-center justify-center rounded border font-semibold"
        style={{ background: bg, color: fg }}
        aria-hidden
      >
        Aa
      </span>
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {result ? (
        <>
          <span className="tabular-nums text-muted-foreground">{result.ratio.toFixed(2)}:1</span>
          {allPass ? (
            <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-success" aria-label="Passes all levels" />
          ) : targetPass ? (
            <AlertTriangle
              className="h-3.5 w-3.5 shrink-0 text-warning"
              aria-label={`Meets ${target.toUpperCase()} but not every level`}
            />
          ) : (
            <XCircle className="h-3.5 w-3.5 shrink-0 text-destructive" aria-label={`Fails ${target.toUpperCase()}`} />
          )}
        </>
      ) : (
        <span className="text-muted-foreground">n/a</span>
      )}

      {/* Hover detail: every WCAG level with its verdict and the ratio. */}
      {result && (
        <div className="pointer-events-none absolute right-0 top-full z-30 hidden w-56 rounded-md border bg-background p-2.5 shadow-lg group-hover:block">
          <p className="mb-1.5 font-medium">
            Contrast {result.ratio.toFixed(2)}:1
            <span className="ml-1 font-normal text-muted-foreground">
              ({fg} on {bg})
            </span>
          </p>
          <p className="mb-1.5 text-muted-foreground">
            Target: {target.toUpperCase()} (≥{A11Y_MIN_RATIO[target]}:1) —{" "}
            {targetPass ? "met" : "not met"}
          </p>
          <ul className="space-y-1">
            {result.levels.map((level) => (
              <li key={level.id} className="flex items-center justify-between gap-2">
                <span className="text-muted-foreground">
                  {level.label} <span className="tabular-nums">(≥{level.minRatio}:1)</span>
                </span>
                {level.pass ? (
                  <span className="flex items-center gap-1 font-medium text-success">
                    <CheckCircle2 className="h-3 w-3" /> Pass
                  </span>
                ) : (
                  <span className="flex items-center gap-1 font-medium text-destructive">
                    <XCircle className="h-3 w-3" /> Fail
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
